const CART_DRAWER_AUTO_OPEN_STORAGE_KEY = 'eg-cart-drawer-last-auto-opened-at';
const CART_DRAWER_AUTO_OPEN_COOLDOWN_MS = 30 * 60 * 1000;

class CartDrawer extends HTMLElement {
  constructor() {
    super();

    this.lastAutoOpenedAfterAddAt = 0;
    this.lastAddConfirmationAt = 0;
    this.addConfirmationTimer = null;
    this.memberClaimNormalizationPromise = null;
    this.emptyProductsMediaQuery = window.matchMedia('(min-width: 990px)');
    const mountVisibleEmptyProducts = () => {
      if (this.classList.contains('active')) this.mountEmptyCartProducts();
    };
    if (typeof this.emptyProductsMediaQuery.addEventListener === 'function') {
      this.emptyProductsMediaQuery.addEventListener('change', mountVisibleEmptyProducts);
    } else {
      this.emptyProductsMediaQuery.addListener(mountVisibleEmptyProducts);
    }
    this.addEventListener('keyup', (evt) => evt.code === 'Escape' && this.close());
    this.querySelector('#CartDrawer-Overlay').addEventListener('click', this.close.bind(this));
    this.setHeaderCartIconAccessibility();
    document.addEventListener('cart-drawer:updated', () => this.normalizeProtectedMemberClaims());
  }

  setHeaderCartIconAccessibility() {
    const cartLink = document.querySelector('#cart-icon-bubble');
    cartLink.setAttribute('role', 'button');
    cartLink.setAttribute('aria-haspopup', 'dialog');
    cartLink.addEventListener('click', (event) => {
      event.preventDefault();
      this.open(cartLink);
    });
    cartLink.addEventListener('keydown', (event) => {
      if (event.code.toUpperCase() === 'SPACE') {
        event.preventDefault();
        this.open(cartLink);
      }
    });
  }

  open(triggeredBy) {
    this.mountEmptyCartProducts();
    this.normalizeProtectedMemberClaims();
    if (triggeredBy) this.setActiveElement(triggeredBy);
    const cartDrawerNote = this.querySelector('[id^="Details-"] summary');
    if (cartDrawerNote && !cartDrawerNote.hasAttribute('role')) this.setSummaryAccessibility(cartDrawerNote);
    // here the animation doesn't seem to always get triggered. A timeout seem to help
    setTimeout(() => {
      this.classList.add('animate', 'active');
    });

    this.addEventListener(
      'transitionend',
      () => {
        // Add a small delay to ensure DOM has fully updated
        setTimeout(() => {
          const containerToTrapFocusOn = this.classList.contains('is-empty')
            ? this.querySelector('.drawer__inner-empty')
            : document.getElementById('CartDrawer');
          const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
          
          // Only trap focus if the container exists
          if (containerToTrapFocusOn && focusElement) {
            trapFocus(containerToTrapFocusOn, focusElement);
          }
          
          // Dispatch event for product recommendations
          document.dispatchEvent(new CustomEvent('cart-drawer:opened'));
        }, 50);
      },
      { once: true }
    );

    document.body.classList.add('overflow-hidden');
  }

  mountEmptyCartProducts() {
    // The empty cart's recommendation column is hidden below this breakpoint.
    if (!this.emptyProductsMediaQuery.matches) return;
    const template = this.querySelector('template[data-empty-cart-products]');
    if (!template) return;

    const products = template.parentElement;
    template.replaceWith(document.importNode(template.content, true));
    const picker = window.cardVariantPicker;
    if (picker) {
      picker.initializeAllCards(products);
      picker.setupCustomPreorderCards(products);
      picker.setupPurpleDotManagedCards(products);
    }
    if (typeof window.initializeOkendoStarRatings === 'function') {
      window.initializeOkendoStarRatings(products);
    }
  }

  normalizeProtectedMemberClaims() {
    if (this.memberClaimNormalizationPromise) {
      return this.memberClaimNormalizationPromise;
    }

    const updates = {};
    this.querySelectorAll('[data-member-claim="true"][data-line-key]').forEach((lineItem) => {
      const quantityInput = lineItem.querySelector('.quantity__input');
      if (lineItem.dataset.lineKey && Number(quantityInput?.value) !== 1) {
        updates[lineItem.dataset.lineKey] = 1;
      }
    });

    if (!Object.keys(updates).length) {
      return Promise.resolve(false);
    }

    const body = JSON.stringify({
      updates,
      sections: this.getSectionsToRender().map((section) => section.id),
      sections_url: window.location.pathname,
    });

    this.memberClaimNormalizationPromise = fetch(routes.cart_update_url, {
      ...fetchConfig(),
      body,
    })
      .then((response) => {
        if (!response.ok) {
          throw new Error(`Unable to normalize protected member claim quantity (${response.status})`);
        }
        return response.json();
      })
      .then((cartData) => this.renderHotZones(cartData))
      .catch((error) => {
        console.error(error);
        return false;
      })
      .finally(() => {
        this.memberClaimNormalizationPromise = null;
      });

    return this.memberClaimNormalizationPromise;
  }

  openAfterAdd(itemCount) {
    const now = Date.now();
    let lastAutoOpenedAt = this.lastAutoOpenedAfterAddAt;

    try {
      const storedTimestamp = Number.parseInt(
        window.sessionStorage.getItem(CART_DRAWER_AUTO_OPEN_STORAGE_KEY) || '0',
        10
      );
      if (Number.isFinite(storedTimestamp)) {
        lastAutoOpenedAt = storedTimestamp;
      }
    } catch (error) {
      // Fall back to the in-memory timestamp when sessionStorage is unavailable.
    }

    const isWithinCooldown =
      lastAutoOpenedAt > 0 &&
      now >= lastAutoOpenedAt &&
      now - lastAutoOpenedAt < CART_DRAWER_AUTO_OPEN_COOLDOWN_MS;

    if (this.classList.contains('active')) {
      if (!isWithinCooldown) {
        this.setLastAutoOpenedAfterAddAt(now);
      }
      return true;
    }

    if (isWithinCooldown) {
      this.showAddConfirmation(itemCount);
      return false;
    }

    this.setLastAutoOpenedAfterAddAt(now);
    this.open();
    return true;
  }

  setLastAutoOpenedAfterAddAt(timestamp) {
    this.lastAutoOpenedAfterAddAt = timestamp;
    try {
      window.sessionStorage.setItem(CART_DRAWER_AUTO_OPEN_STORAGE_KEY, String(timestamp));
    } catch (error) {
      // The in-memory timestamp still prevents repeated opens on this page.
    }
  }

  showAddConfirmation(itemCount) {
    let toast = document.getElementById('CartAddConfirmation');
    if (!toast) {
      toast = document.createElement('button');
      toast.type = 'button';
      toast.id = 'CartAddConfirmation';
      toast.className = 'eg-atc-toast';
      toast.tabIndex = -1;
      toast.setAttribute('aria-live', 'polite');
      toast.setAttribute('aria-atomic', 'true');
      const toastTemplate = document.getElementById('CartAddConfirmationTemplate');

      if (toastTemplate) {
        toast.appendChild(toastTemplate.content.cloneNode(true));
      } else {
        toast.innerHTML = `
          <span class="eg-atc-toast__copy">
            <span class="eg-atc-toast__title">Added to cart</span>
            <span class="eg-atc-toast__count" hidden></span>
          </span>
          <span class="eg-atc-toast__action" aria-hidden="true">View cart</span>
        `;
      }
      toast.addEventListener('click', () => {
        clearTimeout(this.addConfirmationTimer);
        toast.classList.remove('is-visible');
        toast.tabIndex = -1;
        this.open();
      });
      toast.addEventListener('focus', () => clearTimeout(this.addConfirmationTimer));
      toast.addEventListener('blur', () => {
        if (!toast.classList.contains('is-visible')) return;
        clearTimeout(this.addConfirmationTimer);
        this.addConfirmationTimer = setTimeout(() => {
          toast.classList.remove('is-visible');
          toast.tabIndex = -1;
        }, 1000);
      });
      document.body.appendChild(toast);
    }

    const cartBadge = document.querySelector('#cart-icon-bubble .cart-count-bubble');
    const badgeText =
      cartBadge?.querySelector('[aria-hidden="true"]')?.textContent ||
      cartBadge?.querySelector('.visually-hidden')?.textContent ||
      '';
    const resolvedItemCount = Number.isFinite(Number(itemCount))
      ? Number(itemCount)
      : Number.parseInt(badgeText.match(/\d+/)?.[0], 10);
    const countElement = toast.querySelector('.eg-atc-toast__count');

    if (countElement) {
      const hasItemCount = Number.isFinite(resolvedItemCount) && resolvedItemCount > 0;
      countElement.hidden = !hasItemCount;
      if (hasItemCount) {
        countElement.textContent = `${resolvedItemCount} ${resolvedItemCount === 1 ? 'item' : 'items'} in cart`;
      }
      toast.setAttribute(
        'aria-label',
        hasItemCount
          ? `Added to cart. ${countElement.textContent}. Open cart.`
          : 'Added to cart. Open cart.'
      );
    }

    const now = Date.now();
    if (now - this.lastAddConfirmationAt < 500) return;
    this.lastAddConfirmationAt = now;

    const header = document.querySelector('sticky-header, .header-wrapper, header.header');
    const headerBottom = header?.getBoundingClientRect().bottom || 0;
    toast.style.setProperty('--eg-atc-toast-top', `${Math.max(16, Math.round(headerBottom + 12))}px`);

    clearTimeout(this.addConfirmationTimer);
    toast.classList.remove('is-visible');
    toast.tabIndex = 0;
    requestAnimationFrame(() => toast.classList.add('is-visible'));

    if (cartBadge) {
      cartBadge.classList.remove('is-confirmed');
      requestAnimationFrame(() => cartBadge.classList.add('is-confirmed'));
      setTimeout(() => cartBadge.classList.remove('is-confirmed'), 500);
    }

    this.addConfirmationTimer = setTimeout(() => {
      toast.classList.remove('is-visible');
      toast.tabIndex = -1;
    }, 4000);
  }

  close() {
    this.classList.remove('active');
    removeTrapFocus(this.activeElement);
    document.body.classList.remove('overflow-hidden');
  }

  setSummaryAccessibility(cartDrawerNote) {
    cartDrawerNote.setAttribute('role', 'button');
    cartDrawerNote.setAttribute('aria-expanded', 'false');

    if (cartDrawerNote.nextElementSibling.getAttribute('id')) {
      cartDrawerNote.setAttribute('aria-controls', cartDrawerNote.nextElementSibling.id);
    }

    cartDrawerNote.addEventListener('click', (event) => {
      event.currentTarget.setAttribute('aria-expanded', !event.currentTarget.closest('details').hasAttribute('open'));
    });

    cartDrawerNote.parentElement.addEventListener('keyup', onKeyUpEscape);
  }

  renderContents(parsedState) {
    const preservedRecommendationState = this.captureRecommendationState();
    const nextIsEmpty = this.getNextEmptyState(parsedState);
    const drawerInner = this.querySelector('.drawer__inner');
    this.classList.toggle('is-empty', nextIsEmpty);
    drawerInner?.classList.toggle('is-empty', nextIsEmpty);

    this.productId = parsedState.id;

    // KEY FIX: Track whether drawer is already open
    const wasAlreadyOpen = this.classList.contains('active');

    this.getSectionsToRender().forEach((section) => {
      const sectionElement = section.selector
        ? document.querySelector(section.selector)
        : document.getElementById(section.id);
      if (sectionElement) {
        const sectionHtml = parsedState.sections[section.id];
        if (sectionHtml) {
          sectionElement.innerHTML = this.getSectionInnerHTML(sectionHtml, section.selector);
        }
      }
    });

    setTimeout(() => {
      this.classList.toggle('is-empty', nextIsEmpty);
      this.querySelector('.drawer__inner')?.classList.toggle('is-empty', nextIsEmpty);
      this.restoreRecommendationState(preservedRecommendationState);

      const overlay = this.querySelector('#CartDrawer-Overlay');
      if (overlay) {
        const newOverlay = overlay.cloneNode(true);
        overlay.parentNode.replaceChild(newOverlay, overlay);
        newOverlay.addEventListener('click', this.close.bind(this));
      }

      requestAnimationFrame(() => {
        document.dispatchEvent(new CustomEvent('cart-drawer:updated', {
          detail: { source: 'render-contents', cartData: parsedState }
        }));
      });

      // KEY FIX: If drawer was already open, do NOT call this.open()
      // open() relies on a CSS transition from inactive->active,
      // which won't fire if 'active' is already set, causing
      // transitionend to never trigger and the drawer to appear closed
      if (wasAlreadyOpen) {
        this.mountEmptyCartProducts();
        this.openAfterAdd(this.getItemCount(parsedState));
        // Just ensure classes are still correct and re-trap focus
        this.classList.add('animate', 'active');
        document.body.classList.add('overflow-hidden');
        const containerToTrapFocusOn = this.classList.contains('is-empty')
          ? this.querySelector('.drawer__inner-empty')
          : document.getElementById('CartDrawer');
        const focusElement = this.querySelector('.drawer__inner') || this.querySelector('.drawer__close');
        if (containerToTrapFocusOn && focusElement) {
          trapFocus(containerToTrapFocusOn, focusElement);
        }
      } else {
        this.openAfterAdd(this.getItemCount(parsedState));
      }
    }, 90);
  }

  getItemCount(parsedState) {
    const directItemCount = parsedState?.item_count;
    if (directItemCount !== undefined && directItemCount !== null && Number.isFinite(Number(directItemCount))) {
      return Number(directItemCount);
    }

    const cartBubbleHtml = parsedState?.sections?.['cart-icon-bubble'];
    if (!cartBubbleHtml) return undefined;

    const cartBubble = new DOMParser().parseFromString(cartBubbleHtml, 'text/html');
    const countText =
      cartBubble.querySelector('.cart-count-bubble [aria-hidden="true"]')?.textContent ||
      cartBubble.querySelector('.cart-count-bubble .visually-hidden')?.textContent ||
      '';
    const itemCount = Number.parseInt(countText.match(/\d+/)?.[0], 10);

    return Number.isFinite(itemCount) ? itemCount : undefined;
  }

  getNextEmptyState(parsedState) {
    if (parsedState?.item_count === 0) {
      return true;
    }

    const sectionHtml = parsedState?.sections?.['cart-drawer'];
    if (!sectionHtml) {
      return false;
    }

    const doc = new DOMParser().parseFromString(sectionHtml, 'text/html');
    const nextCartDrawer = doc.querySelector('cart-drawer');
    const nextDrawer = doc.querySelector('#CartDrawer');
    const nextDrawerInner = doc.querySelector('.drawer__inner');

    return Boolean(
      nextCartDrawer?.classList.contains('is-empty') ||
      nextDrawerInner?.classList.contains('is-empty') ||
      (nextDrawer && !nextDrawer.querySelector('#CartDrawer-CartItems'))
    );
  }

  renderHotZones(parsedState) {
    if (!parsedState?.sections?.['cart-drawer']) {
      return false;
    }

    const doc = new DOMParser().parseFromString(parsedState.sections['cart-drawer'], 'text/html');
    const nextDrawer = doc.querySelector('#CartDrawer');
    const nextDrawerInner = doc.querySelector('.drawer__inner');
    const currentDrawerInner = this.querySelector('.drawer__inner');

    if (!nextDrawer || !nextDrawerInner || !currentDrawerInner) {
      return false;
    }

    const nextIsEmpty = nextDrawerInner.classList.contains('is-empty') || !nextDrawer.querySelector('#CartDrawer-CartItems');
    // Read the current state from the rendered drawer contents. CartItems updates
    // the outer class before this method runs, so using that class here makes a
    // populated -> empty transition look complete before the empty markup exists.
    const currentIsEmpty = currentDrawerInner.classList.contains('is-empty') || !currentDrawerInner.querySelector('#CartDrawer-CartItems');

    if (nextIsEmpty !== currentIsEmpty) {
      this.renderContents(parsedState);
      return true;
    }

    this.productId = parsedState.id;
    this.classList.toggle('is-empty', nextIsEmpty);
    currentDrawerInner.classList.toggle('is-empty', nextIsEmpty);

    const replaceOuter = (selector) => {
      const currentElement = document.querySelector(selector);
      const nextElement = doc.querySelector(selector);

      if (currentElement && nextElement) {
        currentElement.replaceWith(nextElement);
        return nextElement;
      }

      if (currentElement && !nextElement) {
        currentElement.remove();
      }

      return null;
    };

    const replaceInner = (selector) => {
      const currentElement = document.querySelector(selector);
      const nextElement = doc.querySelector(selector);

      if (currentElement && nextElement) {
        currentElement.innerHTML = nextElement.innerHTML;
      }
    };

    replaceOuter('[data-seed-shipping-progress]');
    const progressElement = replaceOuter('.gwp-upsell-container');
    const selectorElement = replaceOuter('[data-gift-selector]');

    if (!selectorElement) {
      const nextSelector = doc.querySelector('[data-gift-selector]');
      const currentProgress = document.querySelector('.gwp-upsell-container');
      if (nextSelector && currentProgress) {
        currentProgress.insertAdjacentElement('afterend', nextSelector);
      } else if (nextSelector && progressElement) {
        progressElement.insertAdjacentElement('afterend', nextSelector);
      }
    }
    replaceInner('#CartDrawer-CartItems');
    replaceInner('.cart-drawer__footer');

    if (parsedState.sections['cart-icon-bubble']) {
      const bubbleDoc = new DOMParser().parseFromString(parsedState.sections['cart-icon-bubble'], 'text/html');
      const currentBubble = document.getElementById('cart-icon-bubble');
      const nextBubble = bubbleDoc.getElementById('cart-icon-bubble') || bubbleDoc.querySelector('.shopify-section #cart-icon-bubble');

      if (currentBubble && nextBubble) {
        currentBubble.innerHTML = nextBubble.innerHTML;
      }
    }

    requestAnimationFrame(() => {
      document.dispatchEvent(new CustomEvent('cart-drawer:updated', {
        detail: { source: 'hot-zones', cartData: parsedState }
      }));
    });

    return true;
  }

  captureRecommendationState() {
    const desktopSection = document.querySelector('.drawer__column--right .product-recommendations');
    const desktopTrack = desktopSection?.querySelector('#recommendationsTrack');
    const mobileTrack = document.querySelector('.recommendations-track--mobile');
    const mobileContainer = document.querySelector('cart-drawer-items .product-recommendations--mobile');

    return {
      url: desktopSection?.dataset.url || '',
      desktopHtml: desktopTrack?.innerHTML || '',
      mobileHtml: mobileTrack?.innerHTML || '',
      mobileDisplay: mobileContainer?.style.display || ''
    };
  }

  restoreRecommendationState(state) {
    if (!state || !state.url) return;

    const desktopSection = document.querySelector('.drawer__column--right .product-recommendations');
    if (!desktopSection || desktopSection.dataset.url !== state.url) return;

    const desktopTrack = desktopSection.querySelector('#recommendationsTrack');
    if (desktopTrack && state.desktopHtml && !desktopTrack.innerHTML.trim()) {
      desktopTrack.innerHTML = state.desktopHtml;
    }

    const mobileTrack = document.querySelector('.recommendations-track--mobile');
    if (mobileTrack && state.mobileHtml && !mobileTrack.innerHTML.trim()) {
      mobileTrack.innerHTML = state.mobileHtml;
    }

    const mobileContainer = document.querySelector('cart-drawer-items .product-recommendations--mobile');
    if (mobileContainer && state.mobileDisplay) {
      mobileContainer.style.display = state.mobileDisplay;
    }
  }

  getSectionInnerHTML(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector).innerHTML;
  }

  getSectionsToRender() {
    return [
      {
        id: 'cart-drawer',
        selector: '#CartDrawer',
      },
      {
        id: 'cart-icon-bubble',
      },
    ];
  }

  getSectionDOM(html, selector = '.shopify-section') {
    return new DOMParser().parseFromString(html, 'text/html').querySelector(selector);
  }

  setActiveElement(element) {
    this.activeElement = element;
  }
}

customElements.define('cart-drawer', CartDrawer);

window.refreshCartDrawerHotZones = function refreshCartDrawerHotZones(stateOrSections, triggerId) {
  const cartDrawer = document.querySelector('cart-drawer');
  if (!cartDrawer || typeof cartDrawer.renderHotZones !== 'function') {
    return false;
  }

  const parsedState = stateOrSections?.sections
    ? stateOrSections
    : {
        sections: stateOrSections,
        id: triggerId
      };

  return cartDrawer.renderHotZones(parsedState);
};

class CartDrawerItems extends CartItems {
  getSectionsToRender() {
    return [
      {
        id: 'CartDrawer',
        section: 'cart-drawer',
        selector: '.drawer__inner',
      },
      {
        id: 'cart-icon-bubble',
        section: 'cart-icon-bubble',
        selector: '.shopify-section',
      },
    ];
  }
}

customElements.define('cart-drawer-items', CartDrawerItems);
